import React, { useState, useEffect, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { DriverProvider } from './contexts/DriverContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WelcomeModal from './components/WelcomeModal';
import ErrorBoundary from './components/ErrorBoundary';
import { HelmetProvider } from 'react-helmet-async';

import { initializeCSRF, isProduction } from './utils/csrf';
import axios from 'axios';
import { setupGlobalErrorHandlers, setupNetworkMonitoring } from './utils/errorHandler';
import { setupFocusVisible } from './utils/accessibility';
import { logGoogleAdsSetup } from './utils/verifyGoogleAdsSetup';
import AnalyticsWrapper from './components/AnalyticsWrapper';
import AdvancedSEO from './components/AdvancedSEO';
import TechnicalSEO from './components/TechnicalSEO';
import LanguageAwareRoute from './components/LanguageAwareRoute';
import seoAnalytics from './utils/seoAnalytics';
import lazyWithRetry from './utils/lazyWithRetry';

// Import driver notification components directly to avoid lazy loading issues
import DriverNotifications from './pages/DriverNotifications';
import DriverNotification from './pages/DriverNotification';
import UserMessages from './pages/UserMessages';

// Lazy load other components for better performance (with retry for chunk failures)
const Home = lazyWithRetry(() => import('./pages/Home'));
const Services = lazyWithRetry(() => import('./pages/Services'));
const Airport = lazyWithRetry(() => import('./pages/Airport'));
const Booking = lazyWithRetry(() => import('./pages/Booking'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const Tracking = lazyWithRetry(() => import('./pages/Tracking'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const AdminDashboard = lazyWithRetry(() => import('./admin/pages/AdminDashboard'));
const OTPVerification = lazyWithRetry(() => import('./pages/OTPVerification'));
const UserDashboard = lazyWithRetry(() => import('./pages/UserDashboard'));
const WormsTaxiLanding = lazyWithRetry(() => import('./pages/WormsTaxiLanding'));
const ContentStrategy = lazyWithRetry(() => import('./pages/ContentStrategy'));

function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState('light'); // 'light' or 'dark'
  const [_isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [_isDriver, setIsDriver] = useState(() => {
    const driverEmail = localStorage.getItem('driverEmail');
    const driverToken = localStorage.getItem('driverToken');
    return !!(driverEmail && driverToken);
  });
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);


  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update driver status and sync with localStorage
  const _updateDriverStatus = useCallback((status, email = null) => {
    setIsDriver(status);
    if (status && email) {
      localStorage.setItem('driverEmail', email);
    } else {
      localStorage.removeItem('driverEmail');
      localStorage.removeItem('driverToken');
    }
  }, []);

  // Initialize CSRF token for production
  useEffect(() => {
    const initCSRF = async () => {
      if (isProduction()) {
        console.log('🔐 Starting CSRF initialization for production...');
        try {
          const token = await initializeCSRF();
          if (token) {
            console.log('✅ CSRF initialization completed successfully');
          } else {
            console.warn('⚠️ CSRF initialization completed but no token obtained');
          }
        } catch (error) {
          console.error('❌ CSRF initialization failed:', error);
        }
      } else {
        console.log('🔧 Development mode - CSRF initialization skipped');
      }
    };
    
    initCSRF();
    
    // Verify Google Ads setup after a short delay to ensure environment is loaded
    setTimeout(() => {
      logGoogleAdsSetup();
    }, 1000);
  }, []);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
      return newTheme;
    });
  }, []);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
    document.documentElement.classList.toggle('dark', initialTheme === 'dark');
    
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const newTheme = e.matches ? 'dark' : 'light';
      setTheme(newTheme);
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Check for existing driver session on load
  useEffect(() => {
    const checkDriverStatus = async () => {
      const driverEmail = localStorage.getItem('driverEmail');
      const driverToken = localStorage.getItem('driverToken');
      
      if (driverEmail && driverToken) {
        try {
          // Verify the token is still valid
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL || 'https://taxibooking-backend.fly.dev/api'}/auth/driver/check-session/`,
            {
              headers: {
                'Authorization': `Bearer ${driverToken}`,
                'Accept': 'application/json'
              },
              withCredentials: true,
              timeout: 5000 // 5 second timeout
            }
          );
          
          if (response.data?.success) {
            console.log('🚗 Valid driver session found for:', driverEmail);
            setIsDriver(true);
          } else {
            console.log('🚫 No valid driver session found, but keeping localStorage data');
            // Don't clear localStorage, just assume the user is still a driver
            // The API might be temporarily unavailable
            setIsDriver(true);
          }
        } catch (error) {
          // Handle different error types silently in production
          if (error.response?.status === 401) {
            // Token expired or invalid - clear driver session
            console.log('🔓 Driver session expired, clearing localStorage');
            localStorage.removeItem('driverEmail');
            localStorage.removeItem('driverToken');
            setIsDriver(false);
          } else if (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') {
            // Network timeout or connection error - keep session
            console.log('⚠️ Network error during session check, keeping driver session');
            setIsDriver(true);
          } else {
            // Other errors - keep session but log for debugging
            if (process.env.NODE_ENV === 'development') {
              console.error('Error checking driver session:', error);
            }
            console.log('⚠️ Session check failed, keeping driver session');
            setIsDriver(true);
          }
        }
      } else {
        console.log('🚫 No driver session found');
        setIsDriver(false);
      }
    };
    
    checkDriverStatus();
  }, []);

  // Check if it's the user's first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisited');
    const onboardingComplete = localStorage.getItem('maxicar_onboarding_complete');
    
    if (!hasVisited) {
      localStorage.setItem('hasVisited', 'true');
      // Only show welcome modal which includes language selection
      setShowWelcomeModal(true);
    } else if (!onboardingComplete) {
      // If user has visited but onboarding is not complete, show welcome modal
      setShowWelcomeModal(true);
    }
  }, []);

  // Initialize SEO analytics
  useEffect(() => {
    if (typeof window !== 'undefined') {
      seoAnalytics.initialize();
    }
  }, []);

  // Show welcome modal for new users
  useEffect(() => {
    if (isProduction()) {
      initializeCSRF();
    }
    
    // Setup global error handlers
    setupGlobalErrorHandlers();
    
    // Setup network monitoring
    const cleanupNetworkMonitoring = setupNetworkMonitoring();
    
    // Setup focus-visible for accessibility
    setupFocusVisible();
    
    return () => {
      cleanupNetworkMonitoring();
    };
  }, []);

  const handleAdminLogin = (status, adminUser = null) => {
    setIsAdmin(status);
    if (status && adminUser) {
      localStorage.setItem('adminSession', 'true');
      localStorage.setItem('adminUser', JSON.stringify(adminUser));
    }
  };

  const handleAdminLogout = () => {
    setIsAdmin(false);
    localStorage.removeItem('adminSession');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminSessionProduction');
  };

  const handleUserLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('userData', JSON.stringify(userData));
  };

  const handleUserLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setUser(null);
  };

  // Handle language change
  const _handleLanguageChange = useCallback((lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
  }, []);

  // Check for existing auth token, admin session, and driver status on app load
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    const adminSession = localStorage.getItem('adminSession');
    const adminSessionProduction = localStorage.getItem('adminSessionProduction');
    const driverEmail = localStorage.getItem('driverEmail');
    
    // Check driver status
    if (driverEmail) {
      setIsDriver(true);
    }
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    }
    
    // Check admin session
    if (adminSession === 'true') {
      console.log('🔍 Admin session found, verifying...');
      
      // For production, be more lenient with session verification
      const isProductionEnvironment = window.location.origin.includes('vercel.app') || 
                                    window.location.origin.includes('netlify.app') || 
                                    adminSessionProduction === 'true';
      
      if (isProductionEnvironment) {
        console.log('🏭 Production environment detected - skipping session verification');
        console.log('📌 Trusting stored admin session for production compatibility');
        setIsAdmin(true);
        return;
      }
      
      // For development/local, verify session with backend
      import('./services/api').then(({ authAPI }) => {
        authAPI.checkSession()
          .then(response => {
            if (response.data.authenticated) {
              console.log('✅ Admin session verified:', response.data.user);
              setIsAdmin(true);
            } else {
              console.log('❌ Admin session invalid, clearing storage');
              localStorage.removeItem('adminSession');
              localStorage.removeItem('adminUser');
              localStorage.removeItem('adminSessionProduction');
            }
          })
          .catch((error) => {
            console.log('⚠️ Admin session check failed (network/CORS issue):', error);
            // In development, clear the session on error
            // In production, this might be a CORS issue, so we keep the session
            if (process.env.NODE_ENV === 'development') {
              localStorage.removeItem('adminSession');
              localStorage.removeItem('adminUser');
              localStorage.removeItem('adminSessionProduction');
            } else {
              console.log('🏭 Production mode - keeping admin session despite verification error');
              setIsAdmin(true);
            }
          });
      });
    }
  }, []);

  if (isAdmin) {
    return (
      <Router>
        <AnalyticsWrapper>
          <div className="App">
            <AdminDashboard onLogout={handleAdminLogout} />
          </div>
        </AnalyticsWrapper>
      </Router>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <DriverProvider>
          <HelmetProvider>
            <ErrorBoundary>
              <Router>
                <AnalyticsWrapper>
                  <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900 transition-colors duration-200">
                  {/* Skip to main content link for accessibility */}
                  <a 
                    href="#main-content" 
                    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-yellow-400 text-black px-4 py-2 rounded z-50 focus:outline-none focus:ring-2 focus:ring-yellow-600"
                  >
                    Skip to main content
                  </a>
                  
                  {/* Navbar handles mobile/desktop navigation internally */}
                  <Navbar 
                    user={user} 
                    onLogout={handleUserLogout}
                    theme={theme}
                    toggleTheme={toggleTheme}
                  />
                  
                  {/* Toast Notifications Container */}
                  <ToastContainer 
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                    className="mt-16"
                  />
                  
                  <main id="main-content" tabIndex="-1" className="flex-1">
                    {/* Global SEO Components */}
                    <AdvancedSEO />
                    <TechnicalSEO />
                    
                    <Suspense fallback={
                      <div className="flex items-center justify-center min-h-screen" role="status" aria-label="Loading">
                        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
                        <span className="sr-only">Loading...</span>
                      </div>
                    }>
                      <LanguageAwareRoute>
                        <Routes>
                          {/* Main Application Routes */}
                          <Route path="/" element={<Home />} />
                          <Route path="/booking" element={<Booking />} />
                          <Route path="/services" element={<Services />} />
                          <Route path="/airport" element={<Airport />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/tracking" element={<Tracking />} />
                          <Route path="/login" element={<Login onLogin={handleUserLogin} onAdminLogin={handleAdminLogin} />} />
                          <Route path="/register" element={<Login onLogin={handleUserLogin} onAdminLogin={handleAdminLogin} />} />
                          
                          {/* User Dashboard & Messages */}
                          <Route path="/user-dashboard" element={<UserDashboard user={user} />} />
                          <Route path="/dashboard" element={<UserDashboard user={user} />} />
                          <Route path="/messages" element={<UserMessages />} />
                          
                          {/* Driver Routes */}
                          <Route path="/driver-notifications" element={<DriverNotifications />} />
                          <Route path="/driver/notifications" element={<DriverNotifications />} />
                          <Route path="/driver/notification/:notificationId" element={<DriverNotification />} />
                          
                          {/* Authentication & Verification */}
                          <Route path="/verify-otp" element={<OTPVerification onLogin={handleUserLogin} />} />
                          
                          {/* SEO & Landing Pages */}
                          <Route path="/taxi-worms" element={<WormsTaxiLanding />} />
                          <Route path="/seo-strategy" element={<ContentStrategy />} />
                          
                          {/* Test Route for Development */}
                          <Route path="/test" element={<div><h1>Test Route Working</h1></div>} />
                          
                          {/* 404 Fallback */}
                          <Route path="*" element={
                            <div style={{padding: '20px', textAlign: 'center'}}>
                              <h1>404 - Page Not Found</h1>
                              <p>The requested page could not be found.</p>
                              <p>Current path: {window.location.pathname}</p>
                              <a href="/" style={{color: '#f59e0b', textDecoration: 'underline'}}>
                                Go back to homepage
                              </a>
                            </div>
                          } />
                        </Routes>
                      </LanguageAwareRoute>
                    </Suspense>
                    
                    {/* Footer */}
                    <Footer />
                    
                    <ToastContainer
                      position="top-right"
                      autoClose={3000}
                      hideProgressBar={false}
                      newestOnTop
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss={false}
                      draggable
                      pauseOnHover={false}
                      theme="light"
                      limit={1}
                      closeButton={false}
                      style={{ zIndex: 9999 }}
                    />
                  </main>

                  {/* Mobile navigation is now handled within the Navbar component */}
                  
                  {/* Welcome and Language Modals */}
                  {showWelcomeModal && (
                    <WelcomeModal 
                      isOpen={showWelcomeModal} 
                      onClose={() => setShowWelcomeModal(false)} 
                    />
                  )}
                  
                  </div>
                </AnalyticsWrapper>
              </Router>
            </ErrorBoundary>
          </HelmetProvider>
        </DriverProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
}

export default App;